<template>
    <div class="si-trace">
        <div v-if="isCollapsed" class="collapsed-toggle" @click="expand">
            <span>Agent Thought Trace</span>
            <q-icon name="expand_more" size="14px" class="q-ml-xs"/>
        </div>

        <template v-else>
            <div class="trace-header q-mb-sm" :class="{ 'is-clickable': hasFinished }" @click="collapse">
                <span>{{ header }}</span>
                <q-icon v-if="hasFinished" name="expand_less" size="14px" class="q-ml-xs"/>
            </div>

            <div v-for="(step, index) in visibleSteps" :key="index" class="step-container">
                <div class="line-gutter">
                    <div class="line-path q-mt-xs" v-if="index < visibleSteps.length - 1 || (step.is_expanded && step.details.length)"></div>
                    <div class="icon-node"></div>
                </div>
                <div class="step-content">
                    <div class="step-header" @click="toggle(index)" :style="`padding-bottom: ${step.is_expanded || index === visibleSteps.length - 1 ? '0' : '10'}px;`">
                        <div class="flex items-center">
                            <span class="step-name">{{ step.step_name }}</span>
                            <q-icon v-if="step.details.length" name="expand_more" size="14px" class="q-ml-xs step-chevron" :class="{ 'rotate180': step.is_expanded }"/>
                        </div>
                    </div>
                    <div v-if="step.is_expanded" class="details-section" :style="`padding-bottom: ${index < visibleSteps.length - 1 ? '6' : '0'}px;`">
                        <div v-for="(detail, detailIndex) in step.details" :key="detailIndex" class="detail-item">
                            <div class="detail-icon-node"></div>
                            <div class="detail-content">
                                <div v-for="(line, lineIndex) in String(detail).split('\n')" :key="lineIndex" v-html="markdownToHtml(line)" class="detail-text"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <q-spinner-dots v-if="!hasFinished" size="20px" color="secondary" class="q-ml-xs q-mt-xs"/>
        </template>
    </div>
</template>

<script>
const MIN_REVEAL_DELAY = 480;
const MAX_REVEAL_DELAY = 720;

export default {
    name: "si-thought-trace",

    // `reveal` fires on every step name and every detail, so the panel around the trace
    // can follow the content as it grows past the fold.
    emits: ["complete", "reveal"],

    props: {
        data: {
            type: Array,
            required: true,
            default: () => [],
        },
        // The panel holding the trace tears it down when it is collapsed. Once the reveal has
        // played for an imperative the parent turns this off, so coming back lands straight on
        // the finished, collapsed trace instead of playing it again.
        animate: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            visibleSteps: [],
            hasFinished: false,
            isCollapsed: false,
            // Selecting another imperative replaces `data` while the previous trace is still
            // playing; the timers of that run resolve against a stale token and stop there.
            runToken: 0,
        };
    },

    computed: {
        header() {
            return this.data.find(step => step?.header)?.header || "Agent thought trace";
        },
    },

    watch: {
        data: {
            handler() {
                this.playTrace();
            },
            immediate: true,
        },
    },

    beforeUnmount() {
        this.runToken += 1;
    },

    methods: {
        async playTrace() {
            const token = ++this.runToken;
            this.visibleSteps = [];
            this.hasFinished = false;
            this.isCollapsed = false;

            if (!this.data.length) {
                this.finish(token);
                return;
            }

            if (!this.animate) {
                this.visibleSteps = this.data.map(step => ({
                    step_name: step?.step_name,
                    details: step?.details || [],
                    is_expanded: false,
                }));
                this.finish(token);
                return;
            }

            for (const step of this.data) {
                const details = step?.details || [];
                this.visibleSteps.push({
                    step_name: step?.step_name,
                    details: [],
                    is_expanded: true,
                });
                // Read back through the array so the details land on the reactive copy.
                const visibleStep = this.visibleSteps[this.visibleSteps.length - 1];
                this.$emit("reveal");
                if (!await this.pause(token)) {
                    return;
                }

                for (const detail of details) {
                    visibleStep.details.push(detail);
                    this.$emit("reveal");
                    if (!await this.pause(token)) {
                        return;
                    }
                }
            }

            this.finish(token);
        },

        // Resolves to false when another run took over while the timer was pending.
        async pause(token) {
            await new Promise(res => setTimeout(res, this.revealDelay()));
            return token === this.runToken;
        },

        // The trailing delay of the last reveal doubles as the time to read it before
        // everything folds away into the toggle.
        finish(token) {
            if (token !== this.runToken) {
                return;
            }
            this.hasFinished = true;
            this.collapse();
            this.$emit("complete");
        },

        revealDelay() {
            return MIN_REVEAL_DELAY + Math.random() * (MAX_REVEAL_DELAY - MIN_REVEAL_DELAY);
        },

        expand() {
            this.isCollapsed = false;
        },

        collapse() {
            if (!this.hasFinished) {
                return;
            }
            this.visibleSteps.forEach(step => {
                step.is_expanded = false;
            });
            this.isCollapsed = true;
        },

        toggle(index) {
            const step = this.visibleSteps[index];
            if (step.details.length) {
                step.is_expanded = !step.is_expanded;
            }
        },

        markdownToHtml(markdown) {
            markdown = markdown.replace(/\*\*(.*?)\*\*/g, '<b style="font-weight: 600; font-size: 11px;">$1</b>');
            markdown = markdown.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<i>$1</i>');
            markdown = markdown.replace(/^- (.*$)/gm, '<li style="margin-left:14px; font-size: 10px; line-height: 16px;">$1</li>');
            return markdown;
        },
    },
};
</script>

<style scoped>
.si-trace {
    width: 100%;
    min-width: 0;
}

.collapsed-toggle {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    color: #424242;
}

.collapsed-toggle:hover {
    color: #1f1f1f;
}

.trace-header {
    display: flex;
    align-items: center;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #424242;
}

.trace-header.is-clickable {
    cursor: pointer;
}

.trace-header.is-clickable:hover {
    color: #1f1f1f;
}

.step-container {
    display: flex;
    position: relative;
    width: 100%;
    min-width: 0;
}

.line-gutter {
    flex-shrink: 0;
    width: 16px;
    position: relative;
    display: flex;
    justify-content: center;
}

.line-path {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background-image: linear-gradient(to bottom, #f3b699 40%, transparent 0%);
    background-size: 1px 6px;
    background-repeat: repeat-y;
}

.icon-node {
    width: 7px;
    height: 7px;
    background-color: #ff4e00;
    border-radius: 50%;
    margin-top: 4px;
    z-index: 1;
}

.step-content {
    flex-grow: 1;
    min-width: 0;
}

.step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    min-height: 16px;
}

.step-name {
    font-size: 11px;
    font-weight: 600;
    color: #424242;
}

.step-header:hover .step-name,
.step-header:hover .step-chevron {
    color: #1f1f1f;
}

.step-chevron {
    color: #757575;
    transition: transform 0.15s ease;
}

.rotate180 {
    transform: rotate(180deg);
}

.details-section {
    margin-top: 4px;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    margin-left: 2px;
    margin-top: 6px;
    min-width: 0;
}

.detail-icon-node {
    flex-shrink: 0;
    width: 5px;
    height: 5px;
    background-color: #f5966e;
    border-radius: 50%;
    margin-right: 8px;
    margin-top: 5px;
}

.detail-content {
    min-width: 0;
    line-height: 1.5;
    color: #5f5f5f;
}

.detail-text {
    font-size: 10px;
    line-height: 16px;
    overflow-wrap: anywhere;
}
</style>
